import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
  type ChartOptions,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import { cs } from "date-fns/locale";

import { init, getCSRFToken } from "@utilities";
import { Tabs, HorizontalScrollPanel } from "@modules";
import { LineChartProps } from "./types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale
);

const ChartComponent: React.FC<LineChartProps> = ({ localization }) => {
  const chartRef = useRef<any>(null);

  // Date Initialization
  const today = new Date();
  const defaultToDate = useMemo(() => new Date("2025-06-03"), []);
  const defaultFromDate = useMemo(() => {
    const d = new Date(defaultToDate);
    d.setFullYear(d.getFullYear() - 1);
    return d;
  }, [defaultToDate]);

  const [gradient, setGradient] = useState<string | CanvasGradient>("");
  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [unit, setUnit] = useState<string>("");
  const [periodDate, setPeriodDate] = useState<string>("");

  const [widgetDataItems, setWidgetDataItems] = useState<any[]>([]);
  const [widgetFundCodes, setWidgetFundCodes] = useState<string[]>([]);

  const [selectedDateFrom, setSelectedDateFrom] = useState(defaultFromDate);
  const [selectedDateTo, setSelectedDateTo] = useState(defaultToDate);
  const [dateRangeOpened, setDateRangeOpened] = useState(false);

  const applyGradient = useCallback(() => {
    if (!chartRef.current) return;
    const { ctx, chartArea } = chartRef.current;
    if (!ctx || !chartArea) return;

    const gradientFill = ctx.createLinearGradient(0, 0, 0, chartArea.bottom);
    gradientFill.addColorStop(0, "rgba(38, 124, 41, 0.24)");
    gradientFill.addColorStop(1, "rgba(38, 124, 41, 0.00)");
    setGradient(gradientFill);
  }, []);

  useEffect(() => {
    init.multiple(Tabs, document.querySelectorAll(".js-tabs"));
    init.multiple(HorizontalScrollPanel, document.querySelectorAll(".js-horizontal-panel"));
  }, []);

  useEffect(() => {
    const widgetElement = document.querySelector<HTMLElement>("[data-widget_id]");
    const widgetId = widgetElement?.dataset.widget_id;
    const widgetData = (window as any).WIDGETS_CONFIG?.[widgetId];

    if (widgetData) {
      setWidgetFundCodes(widgetData.fundCode || []);
      setWidgetDataItems(widgetData.items || []);
      setUnit(widgetData.actualUnitValueStr || "");
      setPeriodDate(`${widgetData.dateFromStr} až ${widgetData.dateToStr}`);
    }
  }, []);

  useEffect(() => {
    if (widgetDataItems.length) {
      setValues(widgetDataItems.map((i) => i.value));
      setLabels(widgetDataItems.map((i) => i.valuationDate));
    }
  }, [widgetDataItems]);

  // Aktualizace gradientu při změně dat
  useEffect(() => {
    if (chartRef.current) {
      applyGradient();
    }
  }, [labels, values, applyGradient]);

  const fetchData = useCallback(
    async (timeFrame?: number, from?: Date, to?: Date) => {
      const payload = {
        dateFrom: from?.toISOString() || "2020-01-01T00:00:00Z",
        dateTo: to?.toISOString() || "2025-01-01T00:00:00Z",
        fundCode: widgetFundCodes,
        timeFrame,
      };

      try {
        const res = await fetch(`${window.location.origin}/api/LineChart/List`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            __RequestVerificationToken: getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const json = await res.json();
        const { items } = json.payload || {};

        if (items?.length) {
          setValues(items.map((i: any) => i.value));
          setLabels(items.map((i: any) => i.valuationDate));
        }
      } catch (err) {
        console.error("Error fetching chart data:", err);
      }
    },
    [widgetFundCodes]
  );
  const showDateRange = () => {
    setDateRangeOpened(true);
  };

  const handleTabClick = (timeFrame: number, showDatePickers?: boolean) => {
    if (showDatePickers) {
      showDateRange();
      return;
    }
    setDateRangeOpened(false);
    fetchData(timeFrame);
  };

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "Kurz",
          data: values,
          borderColor: "green",
          backgroundColor: gradient,
          pointRadius: 0,
          tension: 0,
          fill: true,
          borderWidth: 2,
        },
      ],
    }),
    [labels, values, gradient]
  );

  const options: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.parsed.y.toFixed(2).replace(".", ",")} ${localization.LineChart_Currency}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          tooltipFormat: "d.M.yyyy",
          displayFormats: { day: "d.M.yyyy" },
        },
        ticks: { autoSkip: true, maxRotation: 45, minRotation: 45 },
        grid: { display: false },
      },
      y: {
        ticks: {
          callback: (value) =>
            typeof value === "number" ? value.toFixed(2).replace(".", ",") : value,
        },
        grid: {
          color: "#e0e0e0",
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };
  return (
    <div>
      <div className="js-tabs overflow-hidden">
        <h3 className="mb-2 break-words text-2xl">{localization.Chart_Title}</h3>
        <div className="mb-6 text-lg">
          {localization.LineChart_Note_ActualValue} <span className="font-semibold">{unit} </span>
          {localization.LineChart_Note_ForPeriod}{" "}
          <span className="font-semibold">{periodDate}</span>
        </div>
        <div className="-mt-3 sm:-mt-4">
          <div className="overflow-hidden">
            <div className="row">
              <div className="sm:col-24">
                <div className="horizontal-scroll__outer-container">
                  <div className="js-horizontal-panel relative sm:flex sm:justify-center">
                    <ul
                      className="js-tabs-button-list js-horizontal-panel-button-list horizontal-scroll__panel-wrapper"
                      role="tablist"
                    >
                      <li className="horizontal-scroll__panel-item" data-testid="test-id">
                        <button
                          role="tab"
                          type="button"
                          className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                          draggable="false"
                          aria-selected="false"
                          onClick={() => handleTabClick(6)}
                        >
                          <span className="text-xs text-grey-160 sm:text-base">
                            {localization.LineChart_Buttons_6M}
                          </span>
                        </button>
                      </li>
                      <li className="horizontal-scroll__panel-item" data-testid="test-id">
                        <button
                          role="tab"
                          type="button"
                          className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                          draggable="false"
                          aria-selected="false"
                          onClick={() => handleTabClick(12)}
                        >
                          <span
                            className="text-xs text-grey-160 sm:text-base"
                            data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                          >
                            {localization.LineChart_Buttons_1Y}
                          </span>
                        </button>
                      </li>
                      <li className="horizontal-scroll__panel-item" data-testid="test-id">
                        <button
                          role="tab"
                          type="button"
                          className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button is-active"
                          draggable="false"
                          aria-selected="true"
                          onClick={() => handleTabClick(0)}
                        >
                          <span
                            className="text-xs text-grey-160 sm:text-base"
                            data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                          >
                            {localization.LineChart_Buttons_FromBegin}
                          </span>
                        </button>
                      </li>
                      <li className="horizontal-scroll__panel-item" data-testid="test-id">
                        <button
                          role="tab"
                          type="button"
                          className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                          draggable="false"
                          aria-selected="false"
                          onClick={() => {
                            handleTabClick(12, true);
                          }}
                        >
                          <span
                            className="text-xs text-grey-160 sm:text-base"
                            data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                          >
                            {localization.LineChart_Buttons_Custom}
                          </span>
                        </button>
                      </li>
                    </ul>
                    <button
                      className="js-horizontal-panel-prev-button horizontal-scroll__button-arrow horizontal-scroll__button-prev hidden"
                      type="button"
                      aria-label="Předchozí"
                      tabIndex={-1}
                      data-testid="tabs-widget-tabs-scroll-btn-id"
                    >
                      <svg
                        name="arrow-right"
                        className="icon h-8 w-8 shrink-0 rotate-180 fill-red-corporate"
                        role="img"
                        viewBox="0 0 32 32"
                      >
                        <path d="M17.061 16L12.111 11.05L13.525 9.63599L19.889 16L13.525 22.364L12.111 20.95L17.061 16Z" />
                      </svg>
                    </button>
                    <button
                      className="js-horizontal-panel-next-button horizontal-scroll__button-arrow horizontal-scroll__button-next hidden"
                      type="button"
                      aria-label="Další"
                      tabIndex={-1}
                      data-testid="tabs-widget-tabs-scroll-btn-id"
                    >
                      <svg
                        name="arrow-right"
                        className="icon h-8 w-8 shrink-0 fill-red-corporate"
                        role="img"
                        viewBox="0 0 32 32"
                      >
                        <path d="M17.061 16L12.111 11.05L13.525 9.63599L19.889 16L13.525 22.364L12.111 20.95L17.061 16Z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div
                  className={`${
                    dateRangeOpened ? "flex" : "hidden"
                  } flex-wrap items-center justify-center sm:flex-nowrap`}
                >
                  {/* datepicker */}
                  <div className="mb-8 w-full sm:mb-3 sm:mr-8 sm:w-auto">
                    <label className="block max-w-xxs" htmlFor="datepickerFrom">
                      <span className="mb-1.5 block text-2xs">
                        {localization.LineChart_CustomDate_From}
                      </span>
                    </label>
                    <DatePicker
                      id="datepickerFrom"
                      selected={selectedDateFrom}
                      className="h-12 rounded-xs border border-grey-30 !px-3 py-2.5 text-base outline-grey-80"
                      onChange={(date) => {
                        setSelectedDateFrom(date as Date);
                        if (selectedDateTo && date) {
                          fetchData(undefined, date as Date, selectedDateTo);
                        }
                      }}
                      locale={cs}
                      dateFormat="dd.MM.yyyy"
                      maxDate={today}
                      showIcon
                      toggleCalendarOnIconClick
                      icon={
                        <svg
                          className="!w-8 !h-8 !right-0"
                          width="32"
                          height="32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.75 5.25v4.5h-1.5v-4.5h1.5Zm8 0v4.5h-1.5v-4.5h1.5Zm-14 7.84a.75.75 0 0 1 .75-.75h17a.75.75 0 0 1 0 1.5h-17a.75.75 0 0 1-.75-.75Z"
                            fill="#E00000"
                          />
                          <path
                            d="M8.737 9.385c-.64.69-.987 1.73-.987 3.115V21c0 1.385.346 2.425.987 3.115.631.68 1.656 1.135 3.263 1.135h8c1.607 0 2.631-.455 3.263-1.135.64-.69.987-1.73.987-3.115v-8.5c0-1.385-.346-2.425-.987-3.115C22.63 8.705 21.607 8.25 20 8.25h-8c-1.607 0-2.632.455-3.263 1.135Zm-1.1-1.02C8.633 7.295 10.108 6.75 12 6.75h8c1.893 0 3.369.545 4.362 1.615.984 1.06 1.388 2.52 1.388 4.135V21c0 1.615-.404 3.075-1.388 4.135-.993 1.07-2.469 1.615-4.362 1.615h-8c-1.893 0-3.368-.545-4.362-1.615-.984-1.06-1.388-2.52-1.388-4.135v-8.5c0-1.615.404-3.075 1.388-4.135Z"
                            fill="#E00000"
                          />
                        </svg>
                      }
                    />
                  </div>
                  <div className="mb-8 w-full sm:mb-3 sm:mr-8 sm:w-auto">
                    <label className="block max-w-xxs" htmlFor="datepickerTo">
                      <span className="mb-1.5 block text-2xs">
                        {localization.LineChart_CustomDate_To}
                      </span>
                    </label>
                    <DatePicker
                      id="datepickerTo"
                      selected={selectedDateTo}
                      className="h-12 rounded-xs border border-grey-30 !px-3 py-2.5 text-base outline-grey-80"
                      onChange={(date) => {
                        setSelectedDateTo(date as Date);
                        if (selectedDateFrom && date) {
                          fetchData(undefined, selectedDateFrom, date as Date);
                        }
                      }}
                      locale={cs}
                      dateFormat="dd.MM.yyyy"
                      maxDate={today}
                      showIcon
                      toggleCalendarOnIconClick
                      icon={
                        <svg
                          className="!w-8 !h-8 !right-0"
                          width="32"
                          height="32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.75 5.25v4.5h-1.5v-4.5h1.5Zm8 0v4.5h-1.5v-4.5h1.5Zm-14 7.84a.75.75 0 0 1 .75-.75h17a.75.75 0 0 1 0 1.5h-17a.75.75 0 0 1-.75-.75Z"
                            fill="#E00000"
                          />
                          <path
                            d="M8.737 9.385c-.64.69-.987 1.73-.987 3.115V21c0 1.385.346 2.425.987 3.115.631.68 1.656 1.135 3.263 1.135h8c1.607 0 2.631-.455 3.263-1.135.64-.69.987-1.73.987-3.115v-8.5c0-1.385-.346-2.425-.987-3.115C22.63 8.705 21.607 8.25 20 8.25h-8c-1.607 0-2.632.455-3.263 1.135Zm-1.1-1.02C8.633 7.295 10.108 6.75 12 6.75h8c1.893 0 3.369.545 4.362 1.615.984 1.06 1.388 2.52 1.388 4.135V21c0 1.615-.404 3.075-1.388 4.135-.993 1.07-2.469 1.615-4.362 1.615h-8c-1.893 0-3.368-.545-4.362-1.615-.984-1.06-1.388-2.52-1.388-4.135v-8.5c0-1.615.404-3.075 1.388-4.135Z"
                            fill="#E00000"
                          />
                        </svg>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Line className="mb-12" ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

export default ChartComponent;
